import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Surface, ScrollArea } from '../../../../../generic/ui/Surface';
import { createGroupColorMap } from '../../../../../generic/ui/colors';
import LikutPageLayout from './LikutPageLayout';
import LikutSelectBar from './LikutSelectBar';
import LikutAzmanaRowItem from './LikutAzmanaRowItem';
import { IWarhouseListItem } from '../../../../../modules/API/Defaults/models/warhouseList';
import { IOrderGoItem, TCheckForActiveLikutAzmana } from '../types/list';
import { OrderAkpacaTypeValue } from '../../../../../generic/ui/Selectors/OrderAkpacaTypeSelector';

interface Props {
    azmanotList: IOrderGoItem[];
    inputAzmanotSearch: string;
    searchAzmana: (input: string) => void;
    sortedByRamp: boolean;
    sortByRamp: () => void;
    setSelectedDateStart: (value: string) => void;
    tranzitID: number;
    handleSelectTransit: (id: number) => void;
    warehouseList: IWarhouseListItem[] | null;
    typeFilter: OrderAkpacaTypeValue;
    setTypeFilter: (value: OrderAkpacaTypeValue) => void;
    isDev: boolean;
    handleOpenDefaults: () => void;
    setAzmanaNum: (orderID: number) => void;
    checkForActiveLikutAzmana: TCheckForActiveLikutAzmana;
    clientList: { hashTable: Record<number, { RampId?: number }> } | null;
    isAddIdToDesc: boolean;
}

const LikutListView: React.FC<Props> = ({
    azmanotList,
    inputAzmanotSearch,
    searchAzmana,
    sortedByRamp,
    sortByRamp,
    setSelectedDateStart,
    tranzitID,
    handleSelectTransit,
    warehouseList,
    typeFilter,
    setTypeFilter,
    isDev,
    handleOpenDefaults,
    setAzmanaNum,
    checkForActiveLikutAzmana,
    clientList,
    isAddIdToDesc,
}) => {
    const [t] = useTranslation(['likut']);
    const isEmpty = !azmanotList || azmanotList.length < 1;
    const lastIndex = (azmanotList?.length ?? 0) - 1;
    const getGroupColor = useMemo(
        () => createGroupColorMap(azmanotList?.map((o) => o.GroupDesc) ?? []),
        [azmanotList],
    );

    return (
        <LikutPageLayout title={t('likut_orders_main_title')}>
            <BarRow>
                <LikutSelectBar
                    input={inputAzmanotSearch}
                    setInput={searchAzmana}
                    placeholder={t('likut_orders_search_placeholder')}
                    setSelectedDateStart={setSelectedDateStart}
                    sortedByRamp={sortedByRamp}
                    sortByRamp={sortByRamp}
                    tranzitID={tranzitID}
                    handleSelectTransit={handleSelectTransit}
                    warehouseList={warehouseList}
                    typeFilter={typeFilter}
                    setTypeFilter={setTypeFilter}
                    isDev={isDev}
                    handleOpenDefaults={handleOpenDefaults}
                />
            </BarRow>
            <Surface>
                <ScrollArea>
                    {isEmpty ? (
                        <EmptyState>
                            <img src='/images/no-data.png' alt='empty data' />
                        </EmptyState>
                    ) : (
                        azmanotList.map((order, idx) => (
                            <LikutAzmanaRowItem
                                key={order.OrderID}
                                order={order}
                                setAzmanaNum={setAzmanaNum}
                                checkForActiveLikutAzmana={checkForActiveLikutAzmana}
                                clientList={clientList}
                                isAddIdToDesc={isAddIdToDesc}
                                groupColor={getGroupColor(order.GroupDesc)}
                                isLast={idx === lastIndex}
                            />
                        ))
                    )}
                </ScrollArea>
            </Surface>
        </LikutPageLayout>
    );
};

export default LikutListView;

const BarRow = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-shrink: 0;
`;

const EmptyState = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 2rem;
`;
